import { AiOutlineCheckCircle, AiOutlineDelete, AiOutlineEdit } from 'react-icons/ai';
import { FaBox, FaCog, FaReceipt, FaUsers } from 'react-icons/fa';
import ListStoreItem from './ListStoreItem';
import { type MerchantStore } from '../../../common/types';
import SuccessMessage from '../../../components/success-message';

interface IProps {
  items: MerchantStore[];
  pageType?: 'pending' | 'approved';
  toggleEditModal: () => any;
  toggleDeleteModal: () => any;
  toggleApproveModal: () => any;
  toggleApiConfigModal: () => any;
  handleEditingItem: (item: MerchantStore) => any;
  handleDeletingItem: (item: MerchantStore) => any;
  handleApprovingItem: (item: MerchantStore) => any;
  handleSelectItem: (item: MerchantStore) => any;
}

interface ActionItem {
  label: string;
  icon: JSX.Element | '';
  type: 'button' | 'link' | 'component';
  link?: string;
  handler?: () => any;
}

const ListStores: React.FC<IProps> = ({
  items: merchantStores,
  pageType = 'approved',
  toggleEditModal,
  toggleApproveModal,
  toggleDeleteModal,
  toggleApiConfigModal,
  handleEditingItem,
  handleApprovingItem,
  handleDeletingItem,
  handleSelectItem
}) => {
  const editItemHandler = (item: MerchantStore): void => {
    toggleEditModal();
    handleEditingItem(item);
  };

  const deleteItemHandler = (item: MerchantStore): void => {
    toggleDeleteModal();
    handleDeletingItem(item);
  };

  const apiConfigHandler = (item: MerchantStore): void => {
    toggleApiConfigModal();
    handleSelectItem(item);
  };

  const approveItemHandler = (item: MerchantStore): void => {
    toggleApproveModal();
    handleApprovingItem(item);
  };

  const getActionsList = (item: MerchantStore): any[] => {
    const actions: ActionItem[] = [];
    if (item.isApproved) {
      actions.push(
        {
          type: 'link',
          label: 'List Devices',
          link: `/admin/stores/${item.id}/devices`,
          icon: <FaBox size={18} />
        },
        {
          type: 'component',
          label: 'API Config',
          icon: (
            <button>
              <span className="px-1 h-full">
                <FaCog
                  size={18}
                  onClick={() => {
                    apiConfigHandler(item);
                  }}
                />
              </span>
            </button>
          )
        },
        {
          type: 'link',
          label: 'Sample Receipts',
          link: `/admin/stores/${item.id}/sample-receipts`,
          icon: <FaReceipt size={18} />
        },
        {
          type: 'link',
          label: 'List Users',
          link: `/admin/stores/${item.id}/users`,
          icon: <FaUsers size={18} />
        },
        {
          type: 'link',
          label: 'Configuration',
          link: `/admin/configurations/store/${item.id}`,
          icon: <FaCog size={18} />
        },
        {
          type: 'component',
          label: 'Edit',
          icon: (
            <button>
              <span className="px-1 h-full">
                <AiOutlineEdit
                  color="blue"
                  size={18}
                  onClick={() => {
                    editItemHandler(item);
                  }}
                />
              </span>
            </button>
          )
        },
        {
          type: 'component',
          label: 'Delete',
          icon: (
            <button
              onClick={() => {
                deleteItemHandler(item);
              }}
              className="text-gray-50 text-sm text-center rounded-md"
            >
              <span className="px-1 h-full">
                <AiOutlineDelete color="red" size={18} />
              </span>
            </button>
          )
        }
      );
    } else {
      actions.push(
        {
          type: 'component',
          label: 'Approve',
          icon: (
            <button
              onClick={() => {
                approveItemHandler(item);
              }}
              className="text-gray-50 text-sm text-center rounded-md"
            >
              <span className="px-1 h-full">
                <AiOutlineCheckCircle color="green" size={18} />
              </span>
            </button>
          )
        },
        {
          type: 'component',
          label: 'Delete',
          icon: (
            <button
              onClick={() => {
                deleteItemHandler(item);
              }}
              className="text-gray-50 text-sm text-center rounded-md"
            >
              <span className="px-1 h-full">
                <AiOutlineDelete color="red" size={18} />
              </span>
            </button>
          )
        }
      );
    }

    return actions;
  };

  if (merchantStores.length === 0) {
    const message = 'No merchant stores found';
    return <SuccessMessage message={message} type="error" />;
  }

  return (
    <>
      <div className="w-full p-2 my-2 overflow-auto rounded-md">
        <table className="w-full p-4 border-b-2 border-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 pl-8 text-sm font-semibold text-left tracking-wide">Store</th>
              <th className="p-2 text-sm font-semibold text-left tracking-wide">Address</th>
              <th className="p-2 text-sm font-semibold text-left tracking-wide">Phone</th>
              <th className="p-2 text-sm font-semibold text-left tracking-wide">Status</th>
              <th className="w-12 p-2 pr-8 text-sm font-semibold tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody className="">
            {merchantStores?.map((store) => {
              return <ListStoreItem key={store.id} item={store} pageType={pageType} actions={getActionsList(store)} />;
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ListStores;
